import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "react-query";

import toast from "react-hot-toast";
import { EvseService, TTopUtilizationRatingParams } from "./evse.service";

export const useEvse = (params: TParams) => {
  const { query, ...restParams } = params;

  return useQuery(
    ["list_evse", params],
    async () => {
      const { data } = await EvseService.getAll({
        ...restParams,
        ...query,
      });
      return data;
    },
    { keepPreviousData: true }
  );
};

export const useInfiniteEvse = (params: Omit<TParams, "page">) => {
  const { query, ...restParams } = params;

  return useInfiniteQuery(
    ["list_evse_infinite", params],
    async ({ pageParam = 1 }) => {
      const { data } = await EvseService.getAll({
        ...restParams,
        ...query,
        page: pageParam,
      });
      return data;
    },
    {
      getNextPageParam: (lastPage: any) => {
        const currentTotal =
          (lastPage?.per_page || 0) * (lastPage?.page || 0);
        return currentTotal < (lastPage?.total || 0)
          ? (lastPage?.page || 0) + 1
          : undefined;
      },
      keepPreviousData: true,
    }
  );
};

export const useDetailEvse = (id?: string) => {
  return useQuery(
    [id],
    async () => {
      const { data } = await EvseService.getDetail(id!);
      return data;
    },
    {
      enabled: id ? true : false,
    }
  );
};

// GET /v1/evses/:id/idling-penalties — dedicated idling-penalty listing for the EVSE detail
// page's Idling tab. Unlike GET /v1/charge-sessions, this endpoint actually implements
// status ("active"/"historical"), payment_status ("paid"/"failed") and min_idle_minutes
// (verified against staging: min_idle_minutes correctly excludes sessions at/under the
// threshold; status correctly splits active vs historical; GET /v1/charge-sessions does not).
export const useEvseIdlingPenalties = (
  evseId: string | undefined,
  params: {
    status?: "active" | "historical";
    payment_status?: "paid" | "failed";
    min_idle_minutes?: number;
    from_date?: string;
    to_date?: string;
    sort_by?: string;
    sort_order?: string;
    page?: number;
    per_page?: number;
  },
  options?: { enabled?: boolean }
) =>
  useQuery(
    ["evse_idling_penalties", evseId, params],
    async () => {
      const { data } = await EvseService.getIdlingPenalties(evseId as string, params);
      return data;
    },
    {
      enabled: Boolean(evseId) && (options?.enabled ?? true),
      keepPreviousData: true,
    }
  );

export const useTopUtilizationRatingEvse = (
  params: TTopUtilizationRatingParams
) =>
  useQuery({
    queryKey: ["top-utilization-rating-evse", params],
    queryFn: async () => {
      const { data } = await EvseService.topUtilizationRating(params);
      return data;
    },
    keepPreviousData: true,
  });

export type RestrictedUserDetailResponse = {
  ok: boolean;
  data?: {
    user: {
      _id: string;
      name: string;
      phone: string;
      email: string;
      status: string;
      remarks?: string;
    };
    evses: Array<{
      _id: string;
      ampeco_evse_id: string;
      model: string;
      current_type: string;
      location?: { _id: string; name: string } | null;
      note?: string; // per-EVSE note — shown as the "Remark" column
    }>;
  };
};

// GET /v1/evses/restricted-users/:userId — see EVSE_DETAIL_BE_API_SPEC.md §7.
export const useRestrictedUserDetail = (userId?: string) =>
  useQuery<RestrictedUserDetailResponse>(
    ["restricted_user_detail", userId],
    async () => {
      const { data } = await EvseService.restrictedUserDetail(userId as string);
      return data;
    },
    { enabled: Boolean(userId) }
  );

export const useListUserEvse = (params?: Object) => {
  return useQuery({
    queryKey: ["list_users_evse", params],
    queryFn: async () => {
      try {
        const { data } = await EvseService.listUser(params);
        return data;
      } catch (error) {
        console.log("error", error);
      }
    },
  });
};

export const useUpdateEvse = (callback?: (data: any) => void) => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ id, dto }: { id: string; dto: Object }) => {
      const { data } = await EvseService.update(id, dto);
      return data;
    },
    {
      onSuccess: (data) => {
        callback?.(data);
        if (data.ok) {
          toast.success("Update Evse Success!");
          queryClient.invalidateQueries(["list_evse"]);
          queryClient.invalidateQueries(["list_chargepoints"]);
        } else {
          toast.error(data?.msg);
        }
      },
      onError(error: any) {
        toast.error(error?.msg);
        return error;
      },
    }
  );
};

export const useAddRestrictedUserEvse = () =>
  useMutation({
    mutationFn: async ({
      dto,
    }: {
      dto: { user_ids: string[]; evse_ids: string[] };
    }) => {
      try {
        const { data } = await EvseService.addRestrictedUser(dto);
        return data;
      } catch (error) {
        console.log("error", error);
      }
    },
  });

export const useRemoveRestrictedUserEvse = () =>
  useMutation({
    mutationFn: async ({
      dto,
    }: {
      dto: { user_ids: string[]; evse_ids: string[] };
    }) => {
      try {
        const { data } = await EvseService.RemoveRestrictedUser(dto);
        return data;
      } catch (error) {
        console.log("error", error);
      }
    },
  });

export const useUpdateRestrictedUserEvse = () =>
  useMutation({
    mutationFn: async (dto: { user_id: string; evse_ids: string[] }) => {
      try {
        const { data } = await EvseService.UpdateRestrictedUser(dto);
        return data;
      } catch (error) {
        console.log("error", error);
      }
    },
  });

export const useCopyRestrictedUsersEvse = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (dto: {
      source_evse_ids: string[];
      target_evse_id: string;
    }) => {
      const { data } = await EvseService.copyRestrictedUsers(dto);
      return data;
    },
    onSuccess: (data) => {
      if (data?.ok) {
        toast.success(
          data?.message || data?.msg || "Restricted users copied successfully!"
        );
        queryClient.invalidateQueries(["listUserRestricted"]);
        queryClient.invalidateQueries(["list_users_evse"]);
      } else {
        toast.error(
          data?.msg || data?.message || "Copy restricted users failed"
        );
      }
    },
    onError: (err: any) => {
      const errorMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Copy restricted users failed";
      toast.error(errorMessage);
    },
  });
};

export const useOnOffSubscriptionPlansEvse = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (dto: {
      evse_id: string;
      subscription_price_enabled: boolean;
      enable_subscription_plans?: string[];
    }) => {
      try {
        const { data } = await EvseService.OnOffSubscriptionPlans(dto);
        if (data.ok) {
          toast.success("On Off Subscription Plans Success!");
          queryClient.invalidateQueries(["list_evse"]);
          queryClient.invalidateQueries(["list_chargepoints"]);
        } else {
          toast.error(data?.msg);
        }
        return data;
      } catch (error) {
        console.log("error", error);
      }
    },
  });
};

export const useOnOffReservationEvse = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (dto: {
      evse_id: string;
      reservation_enabled: boolean;
    }) => {
      try {
        const { data } = await EvseService.OnOffReservation(dto);
        if (data.ok) {
          toast.success("On Off Reservation Success!");
          queryClient.invalidateQueries(["list_evse"]);
          queryClient.invalidateQueries(["list_chargepoints"]);
        } else {
          toast.error(data?.msg);
        }
        return data;
      } catch (error) {
        console.log("error", error);
      }
    },
  });
};

export const useStaticEvse = (type: string) =>
  useQuery({
    queryKey: ["statistics", type],
    queryFn: async () => {
      const { data } = await EvseService.getStatic(type);
      return data;
    },
  });

export const useSyncEvse = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (evseId: string) => {
      const { data } = await EvseService.sync(evseId);
      return data;
    },
    onSuccess: (data) => {
      if (data?.ok) {
        toast.success("Sync successfully!");
        queryClient.invalidateQueries(["list_evse"]);
      } else {
        toast.error(data?.msg || "Sync failed");
      }
    },
    onError: (err: any) => {
      const errorMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Sync failed";
      toast.error(errorMessage);
    },
  });
};

export const useSyncAllEvses = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await EvseService.syncAllEvses();
      return data;
    },
    onSuccess: (data) => {
      if (data?.ok) {
        toast.success(data?.msg || "Sync successfully!");
        queryClient.invalidateQueries(["list_evse"]);
        queryClient.invalidateQueries(["list_chargepoints"]);
      } else {
        toast.error(data?.msg || "Sync failed");
      }
    },
    onError: (err: any) => {
      const errorMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Sync failed";
      toast.error(errorMessage);
    },
  });
};

export const useAmpecoSyncEvse = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ampecoChargepointId: string) => {
      const { data } = await EvseService.ampecoSyncEvse(ampecoChargepointId);
      return data;
    },
    onSuccess: (data) => {
      if (data?.ok) {
        toast.success("Sync successfully!");
        queryClient.invalidateQueries(["list_chargepoints"]);
      } else {
        toast.error(data?.msg || "Sync failed");
      }
    },
    onError: (err: any) => {
      const errorMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Sync failed";
      toast.error(errorMessage);
    },
  });
};

export const useExportPricingEvse = () => {
  return useMutation({
    mutationFn: async (params: Object) => {
      const { data } = await EvseService.exportPricing(params);
      return data;
    },
    onError: (err: any) => {
      const errorMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Export pricing failed";
      toast.error(errorMessage);
    },
  });
};

export const useImportPricingEvse = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (formData: FormData) => {
      const { data } = await EvseService.importPricing(formData);
      return data;
    },
    onSuccess: (data) => {
      if (data?.ok) {
        toast.success(data?.msg || "Import pricing successfully!");
        queryClient.invalidateQueries(["list_evse"]);
      } else {
        toast.error(data?.msg || "Import pricing failed");
      }
    },
    onError: (err: any) => {
      const errorMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Import pricing failed";
      toast.error(errorMessage);
    },
  });
};
